import React from "react";
import { Instagram, Facebook, Phone, MapPin, Mail } from "lucide-react";
import { PWA } from "@/constants/testIds";
import { BRAND, LOGO_URL, NAV_LINKS } from "./data";

const Footer = () => {
  const year = new Date().getFullYear();
  const go = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
  return (
    <footer
      data-testid={PWA.footer}
      className="relative bg-[#1A1A1A] text-[#FAF9F6] pt-20 pb-8 wood-grain-dark"
    >
      {/* Marquee */}
      <div className="overflow-hidden border-y border-white/10 -mt-20 mb-16 py-6 bg-[#121212]">
        <div className="marquee-track text-[#D9B27F]/80 font-serif-display text-3xl md:text-5xl italic whitespace-nowrap">
          {Array.from({ length: 2 }).map((_, k) => (
            <span key={k} className="inline-flex items-center gap-12 pr-12">
              <span>Custom Woodworking</span>
              <span className="text-[#C19A6B]">✦</span>
              <span>Handcrafted in South Florida</span>
              <span className="text-[#C19A6B]">✦</span>
              <span>Est 2018</span>
              <span className="text-[#C19A6B]">✦</span>
              <span>Built To Order</span>
              <span className="text-[#C19A6B]">✦</span>
              <span>Real Craftsmanship</span>
              <span className="text-[#C19A6B]">✦</span>
            </span>
          ))}
        </div>
      </div>

      <div className="w-full max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
        <div className="grid grid-cols-2 md:grid-cols-12 gap-10 md:gap-12">
          <div className="col-span-2 md:col-span-5">
            <div className="flex items-center gap-3">
              <span className="block w-12 h-12 rounded-full overflow-hidden ring-1 ring-[#C19A6B]/40 bg-[#FAF9F6]">
                <img src={LOGO_URL} alt="Precision Woodworking Artistry" className="w-full h-full object-cover" />
              </span>
              <div className="leading-tight">
                <p className="font-serif-display text-xl">Precision</p>
                <p className="text-[10px] tracking-[0.32em] uppercase text-[#C19A6B] font-semibold">
                  Woodworking Artistry
                </p>
              </div>
            </div>
            <p className="mt-6 text-[#FAF9F6]/65 text-sm leading-relaxed font-light max-w-sm">
              Custom-built furniture, cabinetry, and architectural woodwork crafted by hand in
              South Florida. Established 2018.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <a
                href="https://www.instagram.com/precisionwoodworkingartist/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid={PWA.footerSocial("instagram")}
                aria-label="Instagram"
                className="w-10 h-10 border border-white/15 flex items-center justify-center hover:bg-[#C19A6B] hover:text-[#121212] hover:border-[#C19A6B] transition-colors"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href="https://www.facebook.com/Precisionwoodworkingartist"
                target="_blank"
                rel="noopener noreferrer"
                data-testid={PWA.footerSocial("facebook")}
                aria-label="Facebook"
                className="w-10 h-10 border border-white/15 flex items-center justify-center hover:bg-[#C19A6B] hover:text-[#121212] hover:border-[#C19A6B] transition-colors"
              >
                <Facebook className="w-4 h-4" />
              </a>
            </div>
          </div>

          <div className="md:col-span-4">
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#C19A6B] font-semibold mb-5">
              Navigate
            </p>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.id}>
                  <button
                    onClick={() => go(l.href)}
                    className="text-[#FAF9F6]/75 hover:text-[#D9B27F] text-sm transition-colors link-line"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#C19A6B] font-semibold mb-5">
              Contact
            </p>
            <ul className="space-y-4 text-sm">
              <li>
                <a
                  href={`tel:${BRAND.phoneTel}`}
                  data-testid={PWA.footerPhone}
                  className="flex items-start gap-3 text-[#FAF9F6]/85 hover:text-[#D9B27F] transition-colors"
                >
                  <Phone className="w-4 h-4 text-[#C19A6B] mt-0.5" />
                  <span>{BRAND.phone}</span>
                </a>
              </li>
              <li className="flex items-start gap-3 text-[#FAF9F6]/85">
                <MapPin className="w-4 h-4 text-[#C19A6B] mt-0.5" />
                <span>South Florida</span>
              </li>
              <li>
                <a
                  href={`mailto:${BRAND.email}`}
                  className="flex items-start gap-3 text-[#FAF9F6]/85 hover:text-[#D9B27F] transition-colors break-all"
                >
                  <Mail className="w-4 h-4 text-[#C19A6B] mt-0.5 shrink-0" />
                  <span>{BRAND.email}</span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-7 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-[#FAF9F6]/50 text-xs tracking-wide">
            © {year} Precision Woodworking Artistry. All rights reserved. · Handcrafted in South Florida.
          </p>
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#C19A6B] font-semibold">
            Est 2018 · South Florida
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
